import { useState, useRef } from 'react'

function parseLines(raw) {
  return raw
    .split('\n')
    .map(line => line.replace(/^[\s\-\*\•\·\d]+[\.\)]\s*/, '').trim())
    .filter(line => line.length > 1)
}

const RECURRENCE_OPTIONS = [
  { label: 'No repeat',     days: '' },
  { label: 'Daily',         days: '1' },
  { label: 'Every 2 days',  days: '2' },
  { label: 'Weekly',        days: '7' },
  { label: 'Every 2 weeks', days: '14' },
  { label: 'Monthly',       days: '30' },
]

export default function QuickAdd({ onAdd, namespace }) {
  const [text,           setText]           = useState('')
  const [loading,        setLoading]        = useState(false)
  const [flash,          setFlash]          = useState('')
  const [recurrenceDays, setRecurrenceDays] = useState('')
  const [baseAmount,     setBaseAmount]     = useState('')
  const textareaRef = useRef(null)

  const kids  = namespace?.slug === 'nishtha'
  const lines = parseLines(text)
  const count = lines.length

  async function handleSubmit(e) {
    e?.preventDefault()
    if (!text.trim() || loading) return

    const snapshot  = lines
    const snapCount = count
    setText('')
    textareaRef.current?.focus()
    setLoading(true)

    try {
      await Promise.all(
        snapshot.map(line =>
          onAdd({
            title:           line,
            raw_input:       line,
            recurrence_days: recurrenceDays ? parseInt(recurrenceDays) : null,
            base_amount:     baseAmount     ? parseFloat(baseAmount)   : null,
          }),
        ),
      )
      setFlash(snapCount === 1 ? '1 task added' : `${snapCount} tasks added`)
      setTimeout(() => setFlash(''), 2500)
    } catch {
      setText(snapshot.join('\n'))
    } finally {
      setLoading(false)
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSubmit()
  }

  return (
    <div className={`rounded-2xl border shadow-sm p-4 mb-4 ${
      kids ? 'bg-white border-purple-200' : 'bg-white border-gray-200'
    }`}>
      <p className={`text-xs font-semibold uppercase tracking-wide mb-2 ${
        kids ? 'text-purple-400' : 'text-gray-400'
      }`}>
        {kids ? "What's your mission today? 🌟" : 'What needs doing?'}
      </p>

      <form onSubmit={handleSubmit}>
        <textarea
          ref={textareaRef}
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={kids
            ? 'Write your chore here!\nYou can add more than one 😊'
            : 'One task per line — or paste a whole list:\n\nRenew car insurance after June 1\nClean gutters'
          }
          rows={3}
          disabled={loading}
          className="w-full text-sm text-gray-800 placeholder-gray-300 resize-none
            border-0 outline-none focus:ring-0 leading-relaxed disabled:opacity-50"
        />

        {/* ── Options row ── */}
        <div className={`flex flex-wrap items-center gap-3 mt-2 pt-2 border-t ${
          kids ? 'border-purple-100' : 'border-gray-100'
        }`}>
          {/* Recurrence */}
          <label className="flex items-center gap-1.5 text-xs text-gray-500">
            <span>🔁</span>
            <select
              value={recurrenceDays}
              onChange={e => setRecurrenceDays(e.target.value)}
              className={`text-xs rounded-lg border px-2 py-1 focus:outline-none focus:ring-1 ${
                kids
                  ? 'border-purple-200 focus:ring-purple-400 text-purple-700'
                  : 'border-gray-200 focus:ring-indigo-400 text-gray-700'
              }`}
            >
              {RECURRENCE_OPTIONS.map(o => (
                <option key={o.days} value={o.days}>{o.label}</option>
              ))}
            </select>
          </label>

          {/* Base amount — Nishtha only */}
          {kids && (
            <label className="flex items-center gap-1.5 text-xs text-gray-500">
              <span>💰</span>
              <span className="text-gray-400">₹</span>
              <input
                type="number"
                min="0"
                step="0.5"
                placeholder="amount"
                value={baseAmount}
                onChange={e => setBaseAmount(e.target.value)}
                className="w-20 text-xs rounded-lg border border-purple-200 px-2 py-1
                  focus:outline-none focus:ring-1 focus:ring-purple-400 text-purple-700"
              />
            </label>
          )}

          {/* Spacer + submit */}
          <div className="flex-1" />
          <span className="text-xs text-gray-400">
            {flash ? (
              <span className={`font-medium ${kids ? 'text-purple-600' : 'text-emerald-600'}`}>
                ✓ {flash}
              </span>
            ) : (
              <span className="hidden sm:inline">⌘↵ to add</span>
            )}
          </span>

          <button
            type="submit"
            disabled={loading || count === 0}
            className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-medium
              text-white transition focus:outline-none focus:ring-2 focus:ring-offset-1
              disabled:opacity-40 ${
                kids
                  ? 'bg-purple-500 hover:bg-purple-600 focus:ring-purple-400'
                  : 'bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500'
              }`}
          >
            {loading ? (
              <svg className="animate-spin w-3.5 h-3.5" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
              </svg>
            ) : (
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 14 14">
                <path d="M7 1v12M1 7h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            )}
            {loading ? 'Adding…' : `Add ${count > 1 ? `${count} tasks` : 'task'}`}
          </button>
        </div>
      </form>
    </div>
  )
}
